/**
 * @module-isolated analytics-reuniones
 * Helpers de presentación — lenguaje comercial (foco: con qué cliente).
 */

import { formatAnalyticsNumber } from '../../../shared/utils/format-number.js';

export function kpiMap(kpis) {
  const map = {};
  (kpis || []).forEach((k) => {
    if (!k) return;
    map[k.label] = k;
    map[k.id] = k;
  });
  return map;
}

export function kpiValue(map, label) {
  const k = map[label];
  if (!k || k.value == null || k.value === '') return null;
  return k.value;
}

/** Agrupa días YYYY-MM-DD en semanas (lunes–domingo). */
export function aggregateByWeek(dayRows) {
  const weeks = {};
  (dayRows || []).forEach((r) => {
    const label = String(r.label || '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(label)) return;
    const d = new Date(label + 'T12:00:00');
    if (isNaN(d.getTime())) return;
    const day = d.getDay();
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((day + 6) % 7));
    const key = ymd(monday);
    if (!weeks[key]) weeks[key] = { weekStart: key, value: 0, days: 0 };
    weeks[key].value += Number(r.value) || 0;
    weeks[key].days += 1;
  });
  return Object.keys(weeks)
    .sort()
    .map((k) => ({
      label: 'Sem. ' + k.slice(5),
      weekStart: k,
      value: weeks[k].value,
      days: weeks[k].days
    }));
}

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function rankBars(rows, totalHint) {
  const list = (rows || [])
    .filter((r) => r && r.label && r.value != null)
    .map((r) => ({
      label: String(r.label),
      value: Number(r.value) || 0,
      vinculo: r.vinculo || '',
      clienteId: r.clienteId || '',
      kommoContactId: r.kommoContactId || ''
    }))
    .filter((r) => r.value > 0)
    .sort((a, b) => b.value - a.value);
  const sum = list.reduce((s, r) => s + r.value, 0) || 1;
  const total =
    totalHint != null && Number(totalHint) > 0 && Math.abs(Number(totalHint) - sum) < 0.5
      ? Number(totalHint)
      : sum;
  return list.map((r, i) => ({
    ...r,
    rank: i + 1,
    share: Math.round((r.value / total) * 1000) / 10
  }));
}

function vinculoHint(code) {
  if (code === 'kommo') return 'Kommo';
  if (code === 'cliente') return 'Cliente sin Kommo';
  if (code === 'sin_cliente') return 'Sin cliente';
  return '';
}

export function clientRankBars(rows, totalHint) {
  return rankBars(rows, totalHint).map((r) => ({
    ...r,
    vinculoLabel: vinculoHint(r.vinculo)
  }));
}

export function buildMeetingsReading(kpis) {
  const m = kpiMap(kpis);
  const agendadas = Number(kpiValue(m, 'Agendadas')) || 0;
  const cancel = Number(kpiValue(m, 'Canceladas')) || 0;
  const conKommo = Number(kpiValue(m, 'Con contacto Kommo')) || 0;
  const conCliente = Number(kpiValue(m, 'Con cliente (sin Kommo)')) || 0;
  const sinCliente = Number(kpiValue(m, 'Sin cliente')) || 0;
  const kommoUnicos = Number(kpiValue(m, 'Contactos Kommo distintos')) || 0;
  const clientesUnicos = Number(kpiValue(m, 'Clientes distintos')) || 0;
  const prop = Number(kpiValue(m, 'Llegaron a propuesta')) || 0;
  const res = Number(kpiValue(m, 'Llegaron a reserva')) || 0;

  if (!agendadas && !cancel) {
    return {
      tone: 'muted',
      title: 'No hay reuniones en este período',
      body: 'Probá otro rango de fechas.'
    };
  }

  const conAlgunCliente = conKommo + conCliente;
  const pctKommo = agendadas > 0 ? Math.round((conKommo / agendadas) * 100) : 0;
  const pctCliente = agendadas > 0 ? Math.round((conAlgunCliente / agendadas) * 100) : 0;

  if (sinCliente >= Math.max(3, agendadas * 0.4)) {
    return {
      tone: 'warn',
      title: 'Varias reuniones sin cliente identificado',
      body:
        `${formatAnalyticsNumber(agendadas)} agendadas · ` +
        `${formatAnalyticsNumber(conKommo)} con contacto Kommo · ` +
        `${formatAnalyticsNumber(conCliente)} con cliente GIC sin Kommo · ` +
        `${formatAnalyticsNumber(sinCliente)} sin cliente. ` +
        `${formatAnalyticsNumber(kommoUnicos)} contactos Kommo distintos.`
    };
  }

  return {
    tone: pctKommo >= 50 ? 'ok' : 'info',
    title: 'Reuniones por cliente',
    body:
      `${formatAnalyticsNumber(agendadas)} agendadas: ` +
      `${formatAnalyticsNumber(conKommo)} con contacto Kommo (${pctKommo}%), ` +
      `${formatAnalyticsNumber(conCliente)} con cliente sin vínculo Kommo, ` +
      `${formatAnalyticsNumber(sinCliente)} sin cliente. ` +
      `${formatAnalyticsNumber(kommoUnicos)} contactos Kommo · ${formatAnalyticsNumber(clientesUnicos)} clientes GIC. ` +
      `${formatAnalyticsNumber(prop)} a propuesta · ${formatAnalyticsNumber(res)} a reserva. ` +
      `${pctCliente}% tienen algún cliente asociado.`
  };
}
